import React from 'react';
import { ArrowRightOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Descriptions, Table, Tag } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate } from 'react-router-dom';

import ListPageHeader from '@shared/ListPageHeader';
import type {
  InventoryTransformationDetail,
  InventoryTransformationItem,
} from '@services/admin/inventoryTransformationService';

const statusColor = (s: string) => {
  switch (s) {
    case 'applied':           return 'success';
    case 'partially_applied': return 'warning';
    case 'failed':            return 'error';
    case 'rejected':          return 'error';
    case 'pending':           return 'default';
    case 'processing':        return 'processing';
    default:                  return 'default';
  }
};

export default function AdminInventoryTransformationShow() {
  const transformation = useLoaderData() as InventoryTransformationDetail;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isPurposeReallocation = transformation.kind === 'purpose_reallocation';
  const showPartialBanner =
    transformation.status === 'partially_applied' || transformation.status === 'failed';

  const columns = [
    {
      title: t('models.product.name'),
      key: 'product',
      render: (_: unknown, r: InventoryTransformationItem) => (
        <div>
          <div>{r.product_sku}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{r.product_name}</div>
        </div>
      ),
    },
    {
      title: t('models.inventory.batch'),
      dataIndex: 'source_batch',
      render: (v: string | null | undefined) => v ?? '-',
    },
    {
      title: t('models.inventory.expiration_date'),
      dataIndex: 'source_expiration_date',
      render: (v: string | null | undefined) => v ?? '-',
    },
    {
      title: t('models.inventory.manufacturing_date'),
      dataIndex: 'source_manufacturing_date',
      render: (v: string | null | undefined) => v ?? '-',
    },
    {
      title: t('models.inventory_transformation.inventory_type_change'),
      key: 'type_change',
      render: (_: unknown, r: InventoryTransformationItem) => (
        <span>
          <Tag>{r.source_inventory_type_name ?? '-'}</Tag>
          <ArrowRightOutlined style={{ margin: '0 4px' }} />
          <Tag color="blue">{r.target_inventory_type_name ?? '-'}</Tag>
        </span>
      ),
    },
    {
      title: t('models.inventory_transformation_item.quantity'),
      dataIndex: 'quantity',
      align: 'right' as const,
    },
    {
      title: t('models.inventory_transformation_item.note'),
      key: 'note',
      render: (_: unknown, r: InventoryTransformationItem) => (
        <div>
          <div>{r.note ?? '-'}</div>
          {r.status === 'failed' && r.failure_code && (
            <div className="transformation-item-error">
              {t(`errors.${r.failure_code}`, {
                ...((r.failure_interpolations ?? {}) as Record<string, unknown>),
                defaultValue: t('errors.unexpected_error'),
              })}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <ListPageHeader
        basePath="/admin"
        items={[
          'stocks',
          'stocks.inventory_transformations',
          { label: transformation.name || transformation.id, path: `/inventory_transformations/${transformation.id}` },
        ]}
      />

      {showPartialBanner && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('shared.inventory_transformation.partial_failure_banner', {
            failed: transformation.failed_count,
            total: transformation.total_items_count,
          })}
          action={
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/inventory_transformations/new')}
            >
              {t('shared.inventory_transformation.create_new_for_remediation')}
            </Button>
          }
        />
      )}

      <Card title={t('shared.inventory_transformation.basic_info')} bordered={false} style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label={t('models.inventory_transformation.name')}>
            {transformation.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.inventory_transformation.status')}>
            <Tag color={statusColor(transformation.status)}>
              {t(`enums.inventory_transformation.status.${transformation.status}`, transformation.status)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('models.inventory_transformation.kind')}>
            <Tag color={isPurposeReallocation ? 'blue' : 'default'}>
              {t(`enums.inventory_transformation.kind.${transformation.kind}`, transformation.kind)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('models.inventory_transformation.warehouse')}>
            {transformation.warehouse_name ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.inventory_transformation.applicant')}>
            {transformation.applicant?.name ?? '-'}
            {transformation.applicant?.kind === 'merchant_user' && (
              <Tag color="purple" style={{ marginLeft: 8 }}>
                {t('shared.inventory_transformation.applicant_kind.merchant_user', '貨主')}
              </Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.inventory_transformation.approver')}>
            {transformation.kind === 'auto_reclassify'
              ? <span style={{ color: '#999' }}>{t('shared.inventory_transformation.approver.system_auto')}</span>
              : (transformation.approver?.name ?? '-')}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.inventory_transformation.created_at')}>
            {transformation.created_at ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.inventory_transformation.note')} span={2}>
            {transformation.note ?? '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {transformation.triggered_by_rule_snapshot != null && (() => {
        // 已在 services/inventoryTransformationSchemas.ts 的 RuleSnapshotSchema 做完
        // Zod 解析，這裡直接拿型別正確的物件渲染。
        const snapshot = transformation.triggered_by_rule_snapshot;
        return (
          <Card
            title={t('shared.inventory_transformation.triggered_by_rule_snapshot.title')}
            bordered={false}
            style={{ marginBottom: 16 }}
          >
            <Descriptions column={2} size="small">
              <Descriptions.Item label={t('shared.inventory_transformation.triggered_by_rule_snapshot.rule_name')}>
                {snapshot.rule.name}
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.inventory_transformation.triggered_by_rule_snapshot.priority')}>
                {snapshot.rule.priority}
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.inventory_transformation.triggered_by_rule_snapshot.source_type')}>
                <Tag>{snapshot.rule.source_inventory_type.name}</Tag>
                <ArrowRightOutlined style={{ margin: '0 4px' }} />
                <Tag color="blue">{snapshot.rule.target_inventory_type.name}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.inventory_transformation.triggered_by_rule_snapshot.threshold_days')}>
                {snapshot.matched_tier.original_expiration_date_threshold}
              </Descriptions.Item>
              <Descriptions.Item
                label={t('shared.inventory_transformation.triggered_by_rule_snapshot.trigger_condition')}
                span={2}
              >
                {snapshot.matched_tier.trigger_mechanism === 'by_days'
                  ? t('shared.inventory_transformation.triggered_by_rule_snapshot.by_days', {
                      days: snapshot.matched_tier.days_before_expiration,
                    })
                  : t('shared.inventory_transformation.triggered_by_rule_snapshot.by_ratio', {
                      ratio: snapshot.matched_tier.expiration_ratio,
                    })}
              </Descriptions.Item>
              <Descriptions.Item
                label={t('shared.inventory_transformation.triggered_by_rule_snapshot.evaluated_at')}
                span={2}
              >
                {dayjs(snapshot.evaluated_at).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        );
      })()}

      <Card title={t('shared.inventory_transformation.items')} bordered={false}>
        <Table
          columns={columns}
          dataSource={transformation.items}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          rowClassName={(item: InventoryTransformationItem) =>
            item.status === 'failed' ? 'transformation-item-row--failed' : ''
          }
        />
      </Card>
    </div>
  );
}
